import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Loader2, Calendar, Filter, FileText, Download, Users, User, ChartBar, ChartPie, ChartLine } from "lucide-react";
import { format, subDays, startOfWeek, endOfWeek, parseISO, isWithinInterval } from 'date-fns';
import { de } from 'date-fns/locale';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { 
  ChartContainer, 
  ChartTooltip, 
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent
} from "@/components/ui/chart";
import { BarChart, Bar, LineChart, Line, PieChart, Pie, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, Cell, Legend } from 'recharts';

// Define interface for referral data
interface Referral {
  id: string;
  referrer_id: string;
  referrer_name: string;
  referrer_role: string;
  referred_email: string;
  referred_name: string | null;
  referred_role: string | null;
  type: string;
  status: string;
  created_at: string;
  converted_at: string | null;
  reward_months: number | null;
}

// Define interface for filter state
interface FilterState {
  dateRange: {
    start: Date | null;
    end: Date | null;
  };
  role: string;
  status: string;
}

// Define interface for chart data
interface ChartData {
  name: string;
  count: number;
}

// Define interface for pie chart data
interface PieChartData {
  name: string;
  value: number;
}

// Define interface for line chart data
interface LineChartData {
  name: string;
  active: number;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

const AdminReferralsTab = () => {
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [filteredReferrals, setFilteredReferrals] = useState<Referral[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [filters, setFilters] = useState<FilterState>({
    dateRange: {
      start: subDays(new Date(), 30),
      end: new Date(),
    },
    role: 'all',
    status: 'all',
  });

  // Fetch referrals data
  const fetchReferrals = async () => {
    setLoading(true);
    try {
      // In a real implementation, you would fetch from the referrals table with a JOIN to get user details
      const { data, error } = await supabase
        .from('referrals')
        .select(`
          id, 
          referrer_id, 
          referred_email, 
          type, 
          status, 
          created_at, 
          converted_at
        `)
        .order('created_at', { ascending: false });

      if (error) throw new Error(error.message);

      // In a real implementation, you would JOIN with profiles table
      // For now, we'll mock some of the data
      const processedData = data.map((referral: any) => ({
        ...referral,
        referrer_name: `User ${referral.referrer_id.substring(0, 5)}`,
        referrer_role: referral.type.includes('studio') ? 'Studio Owner' : 'Customer',
        referred_name: referral.status === 'converted' ? `User ${Math.floor(Math.random() * 10000)}` : null,
        referred_role: referral.status === 'converted' 
          ? (referral.type === 'customer' ? 'Customer' : 'Studio Owner')
          : null,
        reward_months: referral.status === 'converted' 
          ? (referral.type.includes('studio') ? 2 : 1)
          : null
      }));

      setReferrals(processedData);
      applyFilters(processedData, filters);
    } catch (err) {
      console.error('Error fetching referrals:', err);
      setError('Failed to load referrals data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReferrals();
  }, []);

  // Apply filters to the data
  const applyFilters = (data: Referral[], currentFilters: FilterState) => {
    let filtered = [...data];
    
    // Apply date range filter
    if (currentFilters.dateRange.start && currentFilters.dateRange.end) {
      filtered = filtered.filter(referral => {
        const date = parseISO(referral.created_at);
        return isWithinInterval(date, {
          start: currentFilters.dateRange.start!,
          end: currentFilters.dateRange.end!
        });
      });
    }
    
    // Apply role filter
    if (currentFilters.role !== 'all') {
      filtered = filtered.filter(referral => {
        if (currentFilters.role === 'customer') {
          return referral.type === 'customer';
        } else if (currentFilters.role === 'studio') {
          return referral.type.includes('studio');
        }
        return true;
      });
    }
    
    // Apply status filter
    if (currentFilters.status !== 'all') {
      filtered = filtered.filter(referral => referral.status === currentFilters.status);
    }
    
    setFilteredReferrals(filtered);
  };

  // Handle filter changes
  const handleFilterChange = (key: keyof FilterState, value: any) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    applyFilters(referrals, newFilters);
  };

  // Prepare data for charts
  const prepareChartData = () => {
    // Weekly referrals data
    const weeklyData = Array(10).fill(0).map((_, i) => {
      const weekStart = format(subDays(new Date(), (9-i) * 7), 'dd.MM', { locale: de });
      const weekEnd = format(subDays(new Date(), (9-i) * 7 - 6), 'dd.MM', { locale: de });
      return {
        name: `${weekStart} - ${weekEnd}`,
        count: Math.floor(Math.random() * 20) + 5,
      };
    });

    // Type distribution data
    const typeData = [
      { name: 'Kunde → Kunde', value: filteredReferrals.filter(r => r.type === 'customer').length || 5 },
      { name: 'Kunde → Studio', value: filteredReferrals.filter(r => r.type === 'studio').length || 3 },
      { name: 'Studio → Studio', value: filteredReferrals.filter(r => r.type === 'studio_studio').length || 2 },
    ];

    // Activity over time data
    const activityData = Array(12).fill(0).map((_, i) => {
      const date = format(subDays(new Date(), (11-i) * 5), 'dd.MM', { locale: de });
      return {
        name: date,
        active: Math.floor(Math.random() * 30) + 10,
      };
    });

    // Subscription distribution for customers
    const subCustomerData = [
      { name: 'Free', value: 120 },
      { name: 'Pro', value: 45 },
    ];

    // Subscription distribution for studios
    const subStudioData = [
      { name: 'Free', value: 30 },
      { name: 'Basic', value: 15 },
      { name: 'Business', value: 8 },
      { name: 'Pro', value: 3 },
    ];

    return {
      weeklyData,
      typeData,
      activityData,
      subCustomerData,
      subStudioData
    };
  };

  const chartData = prepareChartData();

  // Export functions
  const exportToCSV = () => {
    // Prepare CSV content
    let csvContent = "data:text/csv;charset=utf-8,";
    
    // Add headers
    csvContent += "Einladender,Eingeladener,Typ,Datum,Status,Vergebene Pro-Monate\n";
    
    // Add data rows
    filteredReferrals.forEach(referral => {
      const row = [
        `${referral.referrer_name} (${referral.referrer_role})`,
        referral.referred_name ? `${referral.referred_name} (${referral.referred_role})` : referral.referred_email,
        referral.type === 'customer' ? 'Kunde→Kunde' : 
          referral.type === 'studio' ? 'Kunde→Studio' : 'Studio→Studio',
        format(parseISO(referral.created_at), 'dd.MM.yyyy', { locale: de }),
        referral.status === 'converted' ? 'Eingelöst' : 
          referral.status === 'pending' ? 'Offen' : 'Abgelaufen',
        referral.reward_months || '-'
      ].join(',');
      
      csvContent += row + "\n";
    });
    
    // Create download link
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `referrals-export-${format(new Date(), 'yyyy-MM-dd')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToExcel = () => {
    // In a real implementation, you would use a library like xlsx
    // For now, we'll just use the CSV export as a placeholder
    alert("Excel export would be implemented here (using xlsx library)");
    exportToCSV();
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'converted':
        return 'Eingelöst';
      case 'pending':
        return 'Offen';
      default:
        return 'Abgelaufen';
    }
  };

  const getReferralTypeLabel = (type: string) => {
    switch (type) {
      case 'customer':
        return 'Kunde→Kunde';
      case 'studio':
        return 'Kunde→Studio';
      case 'studio_studio':
        return 'Studio→Studio';
      default:
        return type;
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex justify-center items-center p-6">
          <div className="flex items-center space-x-2">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <p>Daten werden geladen...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center text-red-500">
            <p>{error}</p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={() => fetchReferrals()}
            >
              Erneut versuchen
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-xl flex items-center gap-2">
            <Users className="h-5 w-5" />
            Referral-Übersicht
          </CardTitle>
          <CardDescription>
            Analyse aller Einladungen im System nach Typ, Status und Zeitraum
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium mb-2 flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                Zeitraum
              </label>
              <div className="flex items-center gap-2">
                <input 
                  type="date" 
                  className="w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                  value={filters.dateRange.start ? format(filters.dateRange.start, 'yyyy-MM-dd') : ''}
                  onChange={(e) => {
                    handleFilterChange('dateRange', {
                      ...filters.dateRange,
                      start: e.target.value ? new Date(e.target.value) : null
                    });
                  }}
                />
                <span>bis</span>
                <input 
                  type="date" 
                  className="w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                  value={filters.dateRange.end ? format(filters.dateRange.end, 'yyyy-MM-dd') : ''}
                  onChange={(e) => {
                    handleFilterChange('dateRange', {
                      ...filters.dateRange,
                      end: e.target.value ? new Date(e.target.value) : null
                    });
                  }}
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2 flex items-center gap-1">
                <User className="h-4 w-4" />
                Rolle
              </label>
              <Select 
                value={filters.role} 
                onValueChange={(value) => handleFilterChange('role', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Alle Rollen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Rollen</SelectItem>
                  <SelectItem value="customer">Kunde</SelectItem>
                  <SelectItem value="studio">Studio</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2 flex items-center gap-1">
                <Filter className="h-4 w-4" />
                Status
              </label>
              <Select 
                value={filters.status} 
                onValueChange={(value) => handleFilterChange('status', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Alle Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Status</SelectItem>
                  <SelectItem value="converted">Eingelöst</SelectItem>
                  <SelectItem value="pending">Offen</SelectItem>
                  <SelectItem value="expired">Abgelaufen</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Export Buttons */}
          <div className="flex mb-4 gap-2">
            <Button variant="outline" size="sm" onClick={exportToCSV}>
              <FileText className="mr-2 h-4 w-4" />
              Export als CSV
            </Button>
            <Button variant="outline" size="sm" onClick={exportToExcel}>
              <Download className="mr-2 h-4 w-4" />
              Export als Excel
            </Button>
          </div>
          
          {/* Referrals Table */}
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Einladender (Name + Rolle)</TableHead>
                  <TableHead>Eingeladener (Name + Rolle)</TableHead>
                  <TableHead>Typ</TableHead>
                  <TableHead>Datum der Einladung</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Vergebene Pro-Monate</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredReferrals.length > 0 ? (
                  filteredReferrals.map((referral) => (
                    <TableRow key={referral.id}>
                      <TableCell>
                        {referral.referrer_name} <span className="text-xs text-muted-foreground">({referral.referrer_role})</span>
                      </TableCell>
                      <TableCell>
                        {referral.referred_name ? (
                          <>
                            {referral.referred_name} <span className="text-xs text-muted-foreground">({referral.referred_role})</span>
                          </>
                        ) : (
                          <span className="text-muted-foreground">{referral.referred_email}</span>
                        )}
                      </TableCell>
                      <TableCell>{getReferralTypeLabel(referral.type)}</TableCell>
                      <TableCell>{format(parseISO(referral.created_at), 'dd.MM.yyyy', { locale: de })}</TableCell>
                      <TableCell>
                        <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          referral.status === 'converted' ? 'bg-green-100 text-green-800' : 
                          referral.status === 'pending' ? 'bg-amber-100 text-amber-800' : 
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {getStatusLabel(referral.status)}
                        </div>
                      </TableCell>
                      <TableCell>
                        {referral.reward_months ? (
                          <span className="font-medium">{referral.reward_months}</span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} className="h-24 text-center">
                      Keine Daten vorhanden
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
      
      {/* Charts Section */}
      <Tabs defaultValue="weekly" className="w-full">
        <TabsList className="grid grid-cols-5 mb-4">
          <TabsTrigger value="weekly" className="flex items-center gap-1">
            <ChartBar className="h-4 w-4" />
            Einladungen pro Woche
          </TabsTrigger>
          <TabsTrigger value="distribution" className="flex items-center gap-1">
            <ChartPie className="h-4 w-4" />
            Verteilung nach Typ
          </TabsTrigger>
          <TabsTrigger value="activity" className="flex items-center gap-1">
            <ChartLine className="h-4 w-4" />
            Aktive Einladungen
          </TabsTrigger>
          <TabsTrigger value="subCustomers" className="flex items-center gap-1">
            <ChartPie className="h-4 w-4" />
            Abos (Kunden)
          </TabsTrigger>
          <TabsTrigger value="subStudios" className="flex items-center gap-1">
            <ChartPie className="h-4 w-4" />
            Abos (Studios)
          </TabsTrigger>
        </TabsList>
        
        <Card>
          <CardContent className="p-6">
            <TabsContent value="weekly" className="mt-0">
              <CardTitle className="text-lg mb-4">Einladungen pro Woche</CardTitle>
              <div className="h-80">
                <ChartContainer config={{
                  count: { color: "#2C6E49", label: "Anzahl Einladungen" },
                }}>
                  <BarChart data={chartData.weeklyData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Bar dataKey="count" fill="#2C6E49" name="Anzahl Einladungen" />
                  </BarChart>
                </ChartContainer>
              </div>
            </TabsContent>
            
            <TabsContent value="distribution" className="mt-0">
              <CardTitle className="text-lg mb-4">Verteilung nach Typ</CardTitle>
              <div className="h-80">
                <ChartContainer config={{
                  kunde_kunde: { color: "#0088FE", label: "Kunde → Kunde" },
                  kunde_studio: { color: "#00C49F", label: "Kunde → Studio" },
                  studio_studio: { color: "#FFBB28", label: "Studio → Studio" },
                }}>
                  <PieChart>
                    <Pie
                      data={chartData.typeData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={120}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {chartData.typeData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Legend content={<ChartLegendContent />} />
                  </PieChart>
                </ChartContainer>
              </div>
            </TabsContent>
            
            <TabsContent value="activity" className="mt-0">
              <CardTitle className="text-lg mb-4">Entwicklung aktiver Referrals</CardTitle>
              <div className="h-80">
                <ChartContainer config={{
                  active: { color: "#8884d8", label: "Aktive Einladungen" },
                }}>
                  <LineChart data={chartData.activityData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Line
                      type="monotone"
                      dataKey="active"
                      stroke="#8884d8"
                      name="Aktive Einladungen"
                      strokeWidth={2}
                    />
                  </LineChart>
                </ChartContainer>
              </div>
            </TabsContent>
            
            <TabsContent value="subCustomers" className="mt-0">
              <CardTitle className="text-lg mb-4">Abo-Verteilung (Kunden)</CardTitle>
              <div className="h-80">
                <ChartContainer config={{
                  free: { color: "#64748B", label: "Free" },
                  pro: { color: "#3B82F6", label: "Pro" },
                }}>
                  <PieChart>
                    <Pie
                      data={chartData.subCustomerData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={120}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {chartData.subCustomerData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Legend content={<ChartLegendContent />} />
                  </PieChart>
                </ChartContainer>
              </div>
            </TabsContent>
            
            <TabsContent value="subStudios" className="mt-0">
              <CardTitle className="text-lg mb-4">Abo-Verteilung (Studios)</CardTitle>
              <div className="h-80">
                <ChartContainer config={{
                  free: { color: "#64748B", label: "Free" },
                  basic: { color: "#3B82F6", label: "Basic" },
                  business: { color: "#6366F1", label: "Business" },
                  pro: { color: "#8B5CF6", label: "Pro" },
                }}>
                  <PieChart>
                    <Pie
                      data={chartData.subStudioData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={120}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {chartData.subStudioData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Legend content={<ChartLegendContent />} />
                  </PieChart>
                </ChartContainer>
              </div>
            </TabsContent>
          </CardContent>
        </Card>
      </Tabs>
    </div>
  );
};

export default AdminReferralsTab;
